import { ReviewConfirmation, SOTTaxStamps } from '@/model/license-review.mjs'
import TableElement from '@/table/TableElement'
import { ChevronLeftIcon, ChevronRightIcon } from '@chakra-ui/icons'
import { Box, Breadcrumb, BreadcrumbItem, BreadcrumbLink, Button, FormControl, Grid, GridItem, Image, Input, Popover, PopoverTrigger, PopoverContent, PopoverBody, Select, SimpleGrid, Spinner, Switch, Table, TableContainer, Tbody, Td, Textarea, Tooltip, Tr, useDisclosure } from '@chakra-ui/react'
import React, { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useParams } from 'react-router-dom'
import { callToGetSOTReviewDetails, setIsCertificateStatusUpdated } from '../ducks/slice'
import { getSOTReviewDetails, getSOTReviewDetailsLoading } from './selector'
import { setQuery } from '@/explore/ducks/slice'
import { regexControlNumber, regexEmployeeIdentificationNumber, sotTaxStampsValidation, textareaLengthValidation } from '@/queue/ducks/validationSchema'
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { licenseDetailsNewApi, updateSOTTaxStampsStatus } from '@/api/apis'
import { getFormattedDate, getFormattedDateTime } from '@/queue/elements/value-formatters/date-formatter.mjs'
import { useForm } from 'react-hook-form'
import ControlledInput from '@/common/ControllerInput'
import { fixTimezoneOffset, trimDate } from '@/queue/utils'
//@ts-ignore
import InputMask from 'react-input-mask';
import { getFormattedTextForManualReview, getSourceTextForApprovalPage } from '@/utils/utils'
import { normalizeRejectedReasonFromApi, reviewRejectedReasonsSot, stepMapping } from '@/utils/constants'
import { MapInteractionCSS } from 'react-map-interaction'
import AppConfig from '@/utils/app-config.mjs'
import s3Utils from '@/utils/s3Utils'
import { getDocumentsRequiredDetails, getDocumentsRequiredDetailsLoading, getIsAssigningTransferDealer, getValidLicenseRecord } from '@/queue/ducks/selectors'
import { callToGetDocumentStatus, callToGetLicenseRecordDetails, setDocumentsRequiredDetails, setDocumentsRequiredDetailsLoading } from '@/queue/ducks/slice'
import ReviewConfirmationModal from '@/common/ReviewConfirmationModal'
import ModalComponent from '@/common/Modal'
import ExploreV2 from '@/explore/Explore-v2'
import FlatDatePickr from '@/common/Flatpickr'

/** SOT period is July 1 (year-1) to June 30 (year). Derive year from end date (ISO or MM/DD/YYYY). */
const getSOTYearFromEndDate = (endDate: string | null | undefined): number | null => {
    if (!endDate || typeof endDate !== 'string') return null;
    const s = endDate.trim();
    if (s.includes('T')) {
        const d = new Date(s);
        return !isNaN(d.getTime()) ? d.getFullYear() : null;
    }
    const parts = s.split(/[/-]/);
        if (parts.length >= 3) {
            const year = parseInt(parts[2], 10);
            if (!isNaN(year) && year >= 1900 && year <= 2100) return year;
            const yearFirst = parseInt(parts[0], 10);
            if (!isNaN(yearFirst) && yearFirst >= 1900 && yearFirst <= 2100) return yearFirst;
        }
    const parsed = new Date(s);
    return !isNaN(parsed.getTime()) ? parsed.getFullYear() : null;
};

/** Get start (July 1, year-1) and end (June 30, year) in ISO format for API. */
const getStartEndDatesFromSOTYear = (year: number): { start: string; end: string } => {
    const startDate = new Date(Date.UTC(year - 1, 6, 1));
    const endDate = new Date(Date.UTC(year, 5, 30));
    return {
        start: startDate.toISOString(),
        end: endDate.toISOString(),
    };
};

/** Convert date string to update-sot-status API format (MM/DD/YYYY). Accepts ISO or MM/DD/YYYY. */
const toMMDDYYYYForAPI = (dateStr: string | null | undefined): string | undefined => {
    if (!dateStr) return undefined;
    const s = dateStr.trim();
    if (!s.includes('T') && /^\d{1,2}\/\d{1,2}\/\d{4}$/.test(s)) return s;
    const d = new Date(s);
    if (isNaN(d.getTime())) return undefined;
    const month = d.getUTCMonth() + 1;
    const day = d.getUTCDate();
    const year = d.getUTCFullYear();
    return `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}/${year}`;
};

const YEARS_PER_PAGE = 12

const SOTYearGridPicker = ({ value, onChange, isInvalid }: { value: number | null; onChange: (year: number | null) => void; isInvalid?: boolean }) => {
    const [rangeStart, setRangeStart] = useState(() => {
        const base = value ?? new Date().getFullYear()
        return Math.floor(base / YEARS_PER_PAGE) * YEARS_PER_PAGE
    })
    const { isOpen, onOpen, onClose } = useDisclosure()

    useEffect(() => {
        if (isOpen) {
            const base = value ?? new Date().getFullYear()
            setRangeStart(Math.floor(base / YEARS_PER_PAGE) * YEARS_PER_PAGE)
        }
    }, [isOpen, value])

    const years = Array.from({ length: YEARS_PER_PAGE }, (_, i) => rangeStart + i)

    const handleSelect = (year: number) => {
        onChange(year)
        onClose()
    }

    return (
        <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-start">
            <PopoverTrigger>
                <div className="date-picker-wrapper">
                    <div className="date-input-group">
                        <Input
                            readOnly
                            value={value != null ? String(value) : ''}
                            placeholder="Select year"
                            className={`transfer-form__input sot-year-picker ${isInvalid ? 'error' : ''}`}
                            sx={{
                                cursor: 'pointer',
                                border: 'none',
                                outline: 'none',
                                boxShadow: 'none',
                                bg: 'transparent',
                                _focus: { border: 'none', boxShadow: 'none', outline: 'none' },
                                _hover: { border: 'none' },
                            }}
                        />
                        <Box className="calendar-icon" sx={{ display: 'flex', alignItems: 'center', padding: '0 8px', cursor: 'pointer' }}>
                            <Image src="/images/ui/calendar-primary.svg" width="24px" height="24px" alt="" />
                        </Box>
                    </div>
                </div>
            </PopoverTrigger>
            <PopoverContent width="auto" _focus={{ boxShadow: 'md' }} sx={{ zIndex: 10 }}>
                <PopoverBody p={0}>
                    <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        px={3}
                        py={2}
                        borderBottomWidth="1px"
                        borderColor="gray.200"
                    >
                        <Box
                            as="button"
                            type="button"
                            onClick={(e: React.MouseEvent) => { e.preventDefault(); setRangeStart((s) => s - YEARS_PER_PAGE); }}
                            sx={{ cursor: 'pointer', p: 1, _hover: { bg: 'gray.100' }, borderRadius: 'md' }}
                            aria-label="Previous years"
                        >
                            <ChevronLeftIcon boxSize={5} />
                        </Box>
                        <Box
                            as="button"
                            type="button"
                            onClick={(e: React.MouseEvent) => { e.preventDefault(); setRangeStart((s) => s + YEARS_PER_PAGE); }}
                            sx={{ cursor: 'pointer', p: 1, _hover: { bg: 'gray.100' }, borderRadius: 'md' }}
                            aria-label="Next years"
                        >
                            <ChevronRightIcon boxSize={5} />
                        </Box>
                    </Box>
                    <SimpleGrid columns={3} spacing={0} p={2} minW="200px">
                        {years.map((year) => (
                            <Box
                                key={year}
                                as="button"
                                type="button"
                                onClick={(e: React.MouseEvent) => { e.preventDefault(); handleSelect(year); }}
                                py={2}
                                px={3}
                                textAlign="center"
                                fontWeight={value === year ? 600 : 400}
                                bg={value === year ? '#0c1e46' : 'transparent'}
                                color={value === year ? 'white' : 'gray.800'}
                                _hover={{ bg: value === year ? '#0c1e46' : 'gray.50' }}
                                sx={{ cursor: 'pointer', borderRadius: 'md' }}
                            >
                                {year}
                            </Box>
                        ))}
                    </SimpleGrid>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    )
}

const SOTApprovalPage = () => {

    const dispatch = useDispatch();
    const params = useParams();
    const SOTReviewDetailsLoading = getSOTReviewDetailsLoading();
    const SOTReviewDetails = getSOTReviewDetails();
    const validLicenseRecordDetails = getValidLicenseRecord()
    const documentsRequiredDetails = getDocumentsRequiredDetails();
    const documentsRequiredDetailsLoading = getDocumentsRequiredDetailsLoading();
    const isAssigningTransferDealer = getIsAssigningTransferDealer();

    const principalBusinessAddress = useRef(null) as unknown as React.MutableRefObject<HTMLInputElement>;
    const physicalBusinessAddress = useRef(null) as unknown as React.MutableRefObject<HTMLInputElement>;

    const [isLoading, setIsLoading] = useState(false);
    const [licenseReview, setLicenseReview] = useState<SOTTaxStamps | null>(
        null
    );
    const [reviewInputVal, setReviewInputVal] = useState<any>({
        rejectReason: "",
        note: "",
    });
    const blob = new Blob();
    const [reviewConfirmation, setReviewConfirmation] = useState<ReviewConfirmation>({
        header: "",
        isCancelBtnDisable: false,
        isConfirmBtnDisable: false,
        isConfirmBtnLoading: false,
        isSaveBtnLoading: false,
        rejectedReason: "",
        note: "",
        error: "",
        status: "",
    });
    const [license, setLicense] = useState<any>();
    const [textareaEvent, setTextareaEvent] = useState<any>(null);
    const [downloadingFile2, setDownloadingFile2] = useState<boolean>(false);
    const [dealer_id, setDealerId] = useState<string>('');

    interface OperationTypes {
        [key: string]: {
            name: string,
            rate: number
        }
    }

    const operationTypes: OperationTypes = {
        "61": {
            name: "Class 1 - Importer of Firearms",
            rate: 1000
        },
        "71": {
            name: "Class 1 - Importer of Firearms (Reduced)",
            rate: 500
        },
        "62": {
            name: "Class 2 - Manufacturer of Firearms",
            rate: 1000
        },
        "72": {
            name: "Class 2 - Manufacturer of Firearms (Reduced)",
            rate: 500
        },
        "63": {
            name: "Class 3 - Dealer in Firearms",
            rate: 500
        }
    };
    const fullURL = (path: string) => `${AppConfig.SOTTaxStampsBaseURL}${encodeURIComponent(path)}`;

    const startDateSpecialTaxPickerRef = useRef<any>(null);
    const endDateSpecialTaxPickerRef = useRef<any>(null);
    const dateReceiptPickerRef = useRef<any>(null);

    const openStartDateSpecialTaxDatePicker = () => {
        if (startDateSpecialTaxPickerRef.current) {
            startDateSpecialTaxPickerRef.current.setOpen(true);
        }
    };

    const openEndDateSpecialTaxDatePicker = () => {
        if (endDateSpecialTaxPickerRef.current) {
            endDateSpecialTaxPickerRef.current.setOpen(true);
        }
    };

    const openReceiptDatePicker = () => {
        if (dateReceiptPickerRef.current) {
            dateReceiptPickerRef.current.setOpen(true);
        }
    };

    const {
        isOpen: isReviewConfirmationMadalOpen,
        onClose: onCloseReviewConfirmationModal,
        onOpen: onOpenReviewConfirmationModal
    } = useDisclosure();
    const {
        isOpen: isSearchNewDealerModalOpen,
        onClose: onCloseSearchNewDealerModal,
        onOpen: onOpenSearchNewDealerModal
    } = useDisclosure();

    useEffect(() => {
        // if (isSearchNewDealerModalOpen) onClose()
    }, [isSearchNewDealerModalOpen])

    useEffect(() => {
        if (params.id && !SOTReviewDetailsLoading && !documentsRequiredDetailsLoading) {
            dispatch(setDocumentsRequiredDetailsLoading(true))
            dispatch(callToGetSOTReviewDetails({
                id: params.id
            }))
        }
    }, [params?.id])

    useEffect(() => {
        if (SOTReviewDetails && !SOTReviewDetailsLoading) {
            const startDateApi = SOTReviewDetails?.special_tax_period_start_date ?? "";
            const endDateApi = SOTReviewDetails?.special_tax_period_end_date ?? "";
            const derivedSotYear = getSOTYearFromEndDate(endDateApi);
            setLicenseReview({
                ...SOTReviewDetails,
                date_of_tax_stamp_receipt: SOTReviewDetails?.date_of_tax_stamp_receipt ? getFormattedDate(trimDate(SOTReviewDetails?.date_of_tax_stamp_receipt)) : "",
                special_tax_period_start_date: startDateApi,
                special_tax_period_end_date: endDateApi,
                sot_year: derivedSotYear ?? undefined,
                control_number: SOTReviewDetails?.control_number ? formatControlNumber(SOTReviewDetails?.control_number) : "",
                display_control_number: SOTReviewDetails?.display_control_number ? formatControlNumber(SOTReviewDetails?.display_control_number) : "",
                employee_identification_number: SOTReviewDetails?.control_number ? formatEmployeeIdentificationNumber(SOTReviewDetails?.employee_identification_number) : "",
                bio_metric_scanner: SOTReviewDetails?.bio_metric_scanner || false,
                name_and_principal_business_address: SOTReviewDetails?.name_and_principal_business_address || "",
                actual_physical_business_address: SOTReviewDetails?.actual_name_and_physical_business_address || SOTReviewDetails?.actual_physical_business_address || ""
            })
            setReviewInputVal({
                rejectReason: normalizeRejectedReasonFromApi(SOTReviewDetails?.rejected_reason ?? null) || "",
                note: SOTReviewDetails?.note || "",
            })
            if (SOTReviewDetails?.license_key && (SOTReviewDetails?.license_key.length === 8 || SOTReviewDetails?.license_key.length === 20)) {
                dispatch(callToGetDocumentStatus({ id: SOTReviewDetails?.license_key }));
                setDealerId(SOTReviewDetails?.dealer_id);
            } else {
                dispatch(setDocumentsRequiredDetails({}));
                dispatch(setDocumentsRequiredDetailsLoading(false));
                setDealerId('');
            }
        }
    }, [SOTReviewDetails])

    useEffect(() => {
        principalBusinessAddress?.current?.setSelectionRange(textareaEvent, textareaEvent);
        physicalBusinessAddress?.current?.setSelectionRange(textareaEvent, textareaEvent);
    }, [licenseReview?.name_and_principal_business_address, licenseReview?.actual_physical_business_address])

    const checkStatus = (currentStatus: string | undefined, status: string) => {
        return currentStatus === status;
    };

    const onDateChange = async (date: Date | null, fieldName: string) => {
        if (!date) {
            setLicenseReview((prevState: any) => ({
                ...prevState,
                [fieldName]: null,
                errors: { ...(prevState.errors || {}), [fieldName]: '' }
            }));
            return;
        }

        console.log({ fieldName: getFormattedDate(date.toDateString()) });

        setLicenseReview((prevState: any) => ({
            ...prevState,
            [fieldName]: getFormattedDate(date.toDateString()),
            errors: { ...(prevState.errors || {}), [fieldName]: '' }
        }));
    };

    const onSOTYearChange = (year: number | null) => {
        if (year == null) {
            setLicenseReview((prevState: any) => ({
                ...prevState,
                sot_year: null,
                special_tax_period_start_date: "",
                special_tax_period_end_date: "",
                errors: { ...(prevState.errors || {}), sot_year: '' }
            }));
            return;
        }
        const { start, end } = getStartEndDatesFromSOTYear(year);
        setLicenseReview((prevState: any) => ({
            ...prevState,
            sot_year: year,
            special_tax_period_start_date: start,
            special_tax_period_end_date: end,
            errors: { ...(prevState.errors || {}), sot_year: '' }
        }));
    };

    const onOpenCertificateStatus = async (status: string) => {
        if (status === "approved") {
            await validateFields();
            setReviewConfirmation((prevState: any) => ({
                ...prevState,
                header: "Do you want to Approve this license",
                status: status
            }))
        } else {
            setReviewConfirmation((prevState: any) => ({
                ...prevState,
                header: "Reason of Rejection",
                status: status
            }))
        }
        onOpenReviewConfirmationModal();
    }

    const updateCertificateStatus = async (status: string, isSaveClicked: boolean = false, isEmailIgnored: boolean = false) => {
        try {
            setReviewConfirmation((prevState: ReviewConfirmation) => ({
                ...prevState,
                isConfirmBtnLoading: !isSaveClicked ? true : isSaveClicked,
                isCancelBtnDisable: true,
                isSaveBtnLoading: isSaveClicked,
            }));
            const response = await updateSOTTaxStampsStatus(
                (status === "approved" || isSaveClicked) ? {
                    dealer_id: license?.dealerId || licenseReview?.dealer_id,
                    id: licenseReview?.id,
                    license_key: licenseReview?.license_key,
                    business_name: licenseReview?.business_name,
                    special_tax_period_start_date: toMMDDYYYYForAPI(licenseReview?.special_tax_period_start_date) ?? licenseReview?.special_tax_period_start_date,
                    special_tax_period_end_date: toMMDDYYYYForAPI(licenseReview?.special_tax_period_end_date) ?? licenseReview?.special_tax_period_end_date,
                    date_of_tax_stamp_receipt: licenseReview?.date_of_tax_stamp_receipt,
                    type_of_operation: licenseReview?.type_of_operation,
                    name_and_principal_business_address: licenseReview?.name_and_principal_business_address,
                    actual_physical_business_address: licenseReview?.actual_physical_business_address,
                    bio_metric_scanner: licenseReview?.bio_metric_scanner,
                    license_path: licenseReview?.license_path,
                    annual_tax_rate: licenseReview?.annual_tax_rate,
                    number_of_locations: licenseReview?.number_of_locations,
                    principal_business_name: licenseReview?.principal_business_name,
                    actual_physical_business_name: licenseReview?.actual_physical_business_name,
                    status,
                    employee_identification_number: licenseReview?.employee_identification_number.split("-").join(""),
                    control_number: licenseReview?.control_number?.split("-").join("") ?? "",
                    display_control_number: licenseReview?.display_control_number?.split("-").join("") || null,
                    rejected_reason: reviewConfirmation?.rejectedReason,
                    note: reviewConfirmation?.note,
                    source: licenseReview?.source,
                    ignoreManualReviewEmail: isEmailIgnored
                } : {
                    dealer_id: licenseReview?.dealer_id,
                    id: licenseReview?.id,
                    license_key: licenseReview?.license_key,
                    status,
                    business_name: licenseReview?.business_name,
                    rejected_reason: reviewConfirmation?.rejectedReason,
                    note: reviewConfirmation?.note,
                    ignoreManualReviewEmail: isEmailIgnored
                }
            );
            if (response.status === 204) {
                dispatch(setIsCertificateStatusUpdated(true));
                // reFetchData();
            }
            // closeReviewConfirmationModal();
            // onClose();
        } catch (err: any) {
            console.log('ERROR', err);
            if (
                err &&
                err?.response &&
                err.response?.status == 404 &&
                err.response?.data &&
                err.response.data?.Error
            ) {
                setLicenseReview((prevState: any) => ({
                    ...prevState,
                    errors: { ...(prevState.errors || {}), 'license_key': err.response.data?.Error }
                }))
            }
            setReviewConfirmation((prevState: ReviewConfirmation) => ({
                ...prevState,
                isConfirmBtnLoading: false,
                isCancelBtnDisable: false,
                isSaveBtnLoading: false,
            }));
        } finally {
            setReviewConfirmation((prevState: ReviewConfirmation) => ({
                ...prevState,
                isConfirmBtnLoading: false,
                isCancelBtnDisable: false,
                isSaveBtnLoading: false,
            }));
            onCloseReviewConfirmationModal();
            dispatch(callToGetSOTReviewDetails({
                id: params.id
            }))
        }
    };

    const validateField = async (name: string, value: string | Date | any) => {
        try {
            if ((["employee_identification_number"].includes(name) && value?.length === 11 && regexEmployeeIdentificationNumber.test(licenseReview?.employee_identification_number as string)) || (["control_number"].includes(name) && value?.length === 16 && regexControlNumber.test(licenseReview?.control_number as string))) {
                setLicenseReview((prevState: any) => ({
                    ...prevState,
                    errors: { ...(prevState.errors || {}), [name]: '' }
                }));
            } else {
                await sotTaxStampsValidation.validateAt(name, { [name]: value });
                setLicenseReview((prevState: any) => ({
                    ...prevState,
                    errors: { ...(prevState.errors || {}), [name]: '' }
                }));
            }
        } catch (error: any) {
            if (error.errors && error.errors.length > 0) {
                setLicenseReview((prevState: any) => ({
                    ...prevState,
                    errors: { ...(prevState.errors || {}), [name]: error.errors[0] }
                }));
            }
        }
    };

    const validateFields = async () => {
        try {
            await sotTaxStampsValidation.validate(licenseReview, { abortEarly: false });
        } catch (error: any) {
            if (error.errors && error.errors.length > 0) {
                const errors = error?.inner?.reduce((acc: { [key: string]: string }, err: any) => {
                    acc[err.path] = err.message;
                    return acc;
                }, {});
                setLicenseReview((prevState: any) => ({
                    ...prevState,
                    errors
                }));
                throw new Error(error?.errors[0]);
            }
        }
    };

    const formatEmployeeIdentificationNumber = (value: string) => {
        if (value) {
            value = value.replace(/[a-zA-Z]/g, '');
            if (value.length > 10) return licenseReview?.employee_identification_number || "";
            else if (value.includes("-")) return value;
            else return value.length > 2 ? `${value?.slice(0, 2)}-${value?.slice(2)}` : value;
        }
        return "";
    };

    const formatControlNumber = (value: string) => {
        if (!value) return ''
        if (value.length !== 13) return value || ""
        else if (value.length === 13) {
            value = value.substring(0, 7) + "-" + value.substring(7, 10) + "-" + value.substring(10)
        }
        return value;
    };

    const onChangeInputs = async (value: string | boolean | any, fieldName: string) => {
        await validateField(fieldName, value as string);
        if (fieldName === "employee_identification_number")
            value = formatEmployeeIdentificationNumber(value as string);
        else if (fieldName === "number_of_locations" && value?.length > 9)
            value = licenseReview?.number_of_locations;
        if (fieldName === "type_of_operation") {
            setLicenseReview((prevState: any) => ({
                ...prevState,
                [fieldName]: value,
                annual_tax_rate: operationTypes[value as string]?.rate,
                errors: { ...prevState.errors, [fieldName]: '', annual_tax_rate: '' }
            }));
            return;
        }
        setLicenseReview((prevState: any) => ({
            ...prevState,
            [fieldName]: value
        }));
    };

    const licenseSchema = yup.object({
        license_key: yup.string().trim().required("License number is required.").matches(/^[0-9]{8}$/, "Invalid license number"),
    })
    const {
        register,
        reset,
        formState: { errors }
    } = useForm({
        resolver: yupResolver(licenseSchema),
        mode: 'onChange',
        values: {
            license_key: ''
        }
    });

    const licenseSearchHandler = () => {
        setIsLoading(true);
        dispatch(setQuery([licenseReview?.license_key as string]))
    }

    function renderLicenseId() {
        return (
            <form onSubmit={(e) => {
                e.preventDefault();
                if (licenseReview?.license_key) {
                    licenseSearchHandler();
                    onOpenSearchNewDealerModal()
                }
            }}>
                <div className="transfer-form__search-result" style={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                    <Input
                        style={{ paddingTop: '4px', minWidth: '180px' }}
                        color={'black'}
                        value={licenseReview?.license_key || ''}
                        placeholder='Search a FFL number, Zip code, Control number, Business Name, City, Email, Phone number here.....'
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                            setLicenseReview((prevState: any) => ({ ...prevState, 'license_key': e.target.value }));
                        }
                        }
                        name="license_key"
                    >
                    </Input>
                    <Button
                        onClick={() => {
                            if (licenseReview?.license_key) {
                                licenseSearchHandler();
                                onOpenSearchNewDealerModal()
                            }
                        }}
                        className="transfer-form__search-btn store-license-btn"
                        isDisabled={!licenseReview?.license_key}
                    >
                        {isLoading && !errors?.license_key ? (
                            <Spinner size="sm" />
                        ) : (
                            <img src="/images/ui/search-icon-blue.svg" />

                        )}
                    </Button>
                </div>
                {licenseReview?.errors && licenseReview?.errors?.license_key ?
                    <Box sx={{ width: '70%' }}>
                        <div className="edit-error">
                            {licenseReview.errors && licenseReview?.errors?.license_key && (
                                <>* Please enter valid license number.</>
                            )}
                        </div>
                    </Box> : ""
                }
            </form>
        );
    }

    const onSave = async () => {
        await validateFields()
        await updateCertificateStatus(licenseReview?.status ? licenseReview?.status.toLowerCase() : "pending", true)
    }

    const downloadFile2 = async (path: string) => {
        setDownloadingFile2(true);
        await s3Utils.getFile(path, 'licenseReader', { public: '' });
        setDownloadingFile2(false);
    };

    const handleDownload = async () => {
        const url = fullURL(licenseReview?.license_path || "");
        try {
            const response = await fetch(url);
            if (!response.ok) {
                throw new Error('Network response was not ok');
            }
            const blob = await response.blob();
            const link = document.createElement('a');
            link.href = URL.createObjectURL(blob);
            link.download = licenseReview?.license_path?.split("/").pop() || "";
            document.body.appendChild(link);
            link.click();
            URL.revokeObjectURL(link.href);
            document.body.removeChild(link);
        } catch (error) {
            console.error('Error downloading the image:', error);
        }
    };

    const downloadIcon2 = () => {
        return (
            <>
                {downloadingFile2 ? (
                    <button className="license_details__download-btn download-license-height">
                        <Spinner size="sm" />
                    </button>
                ) : (
                    <Box
                        display={'inline'}
                        position={'relative'}
                        top={'6px'}
                        pl={2}
                    >
                        <Tooltip
                            hasArrow
                            placement="top"
                            label="Download Original SOT"
                            color={'black'}
                            bg={'white'}
                        >
                            <button
                                className="license_details__download-btn download-license-height"
                                onClick={() => downloadFile2(licenseReview?.object_path as string)}
                                disabled={!licenseReview?.object_path}
                            >
                                <Image src="/images/ui/download.svg" />
                            </button>
                        </Tooltip>
                    </Box>
                )}
            </>
        );
    };

    const downloadIcon = () => {
        return <Box
            display={'inline-block'}
            position={'relative'}
            top={'6px'}
            pl={2}
            cursor={'pointer'}
            onClick={handleDownload}
        >
            <Tooltip
                hasArrow
                placement="top"
                label="Download certificate"
                color={'black'}
                bg={'white'}
            >
                <Image src="/images/ui/download.svg" mr={2} />
            </Tooltip>
        </Box>
    }


    const closeReviewConfirmationModal = () => {
        onCloseReviewConfirmationModal();
        setReviewConfirmation({
            header: "",
            isCancelBtnDisable: false,
            isConfirmBtnDisable: false,
            isConfirmBtnLoading: false,
            isSaveBtnLoading: false,
            rejectedReason: "",
            note: "",
            error: "",
            status: "",
        });
    }

    const disableConfirmButton = () => {
        if (reviewConfirmation.error || (reviewConfirmation.status === "rejected" && !reviewConfirmation.rejectedReason)) {
            return true;
        }
        return false;
    }

    const handleChange = async (event: any) => {
        setReviewConfirmation((prevState: ReviewConfirmation) => ({
            ...prevState,
            rejectedReason: event.target.value
        }));
    };

    const handleTextChange = async (event: any) => {
        const value = event.target.value;
        try {
            await textareaLengthValidation.validateAt('textarea', { 'textarea': value });
            setReviewConfirmation((prevState: ReviewConfirmation) => ({
                ...prevState,
                note: event.target.value,
                error: ''
            }));
        } catch (error: any) {
            if (error.errors && error.errors.length > 0) {
                setReviewConfirmation((prevState: ReviewConfirmation) => ({
                    ...prevState,
                    error: error.errors[0]
                }));
            }
        }
    };

    const callLicenseDetails = async (license_key: any) => {
        const response:any = await licenseDetailsNewApi(license_key)
        setLicense(response)
        setLicenseReview((prevState: any) => ({
            ...prevState, license_key: license_key, business_name: response?.primaryBusinessName,
            errors: { ...(prevState.errors || {}), business_name: '', license_key: '' }
        }))
        setDealerId(response.dealerId)
        setIsLoading(false)
    }

    const ModalLoader = () => {
        return (<div style={{ position: "absolute", top: 0, left: 0, right: 0, bottom: 0, background: "rgba(0,0,0,0.3)", zIndex: "10000", display: "flex", justifyContent: "center", alignItems: "center" }}>
            <Spinner />
        </div>)
    }
    const getFailedStep = (value: string | number | null) => {
        if (value == null || value === '') return '';
        return stepMapping[String(value)] || '';
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', }}>
            {(SOTReviewDetailsLoading || documentsRequiredDetailsLoading) && (
                <Box sx={{ position: 'fixed', zIndex: 1000, height: '100%', width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(0, 0, 0, 0.2)', top: 0, left: 0 }} >
                    <Spinner
                        size="lg"
                    />
                </Box>
            )}
            <Box className="transfer_flex_row" sx={{ marginBottom: 2 }}>
                <Breadcrumb
                    spacing="3px"
                    separator={
                        <ChevronRightIcon className="right-arrow" color="#323232" />
                    }
                >
                    <BreadcrumbItem className="breadcrumb-item">
                        <BreadcrumbLink
                            className="breadcrumb-link"
                            as={Link}
                            to="/admin/sot-tax-stamps"
                        >
                            SOT Tax Stamps
                        </BreadcrumbLink>
                    </BreadcrumbItem>
                    <BreadcrumbItem className="breadcrumb-item">
                        <span className="breadcrumb-link breadcrumb-text">
                            SOT Approval Details
                        </span>
                    </BreadcrumbItem>
                </Breadcrumb>
                <Box sx={{ display: 'flex', justifyContent: { base: 'flex-start', lg: 'flex-end' }, flexGrow: 1, flexWrap: 'wrap', paddingTop: { base: 3, lg: 0 } }}>
                    {licenseReview?.object_path &&
                        <>
                            <TableElement
                                label={'SOT Preview:'}
                                className="license-file-path"
                                renderFunction={downloadIcon}
                                type={'review'}
                            ></TableElement>
                            <TableElement
                                label={'Original File'}
                                className="license-file-path"
                                renderFunction={downloadIcon2}
                                type={'review'}
                            ></TableElement>
                        </>}
                    <Button
                        // background="#0C1E46"
                        colorScheme="blue"
                        background="#ffffff"
                        color="#0C1E46"
                        border="1px solid #0C1E46"
                        padding="8px 20px"
                        marginRight="20px"
                        isLoading={reviewConfirmation.isSaveBtnLoading}
                        sx={{ outline: 'none !important' }}
                        isDisabled={(licenseReview &&
                            licenseReview?.errors &&
                            Object.values(licenseReview?.errors).some((val) => val !== '') ? true : false)}
                        onClick={() => { onSave() }}
                    >
                        Save
                    </Button>
                    <Button
                        leftIcon= {<Image src="/images/ui/success.svg" />}
                        {...(checkStatus(licenseReview?.status, "approved") && { isDisabled: true })}
                        {...({ colorScheme: "green", variant: 'outline' })}
                        padding="8px 20px"
                        marginRight="20px"
                        sx={{ outline: 'none !important' }}
                        onClick={() => { onOpenCertificateStatus("approved") }}
                    >
                        {!checkStatus(licenseReview?.status, "approved") ? "Approve" : "Approved"}
                    </Button>
                    <Button
                        leftIcon= {<Image src="/images/ui/reject.svg" />}
                        {...(checkStatus(licenseReview?.status, "rejected") && { isDisabled: true })}
                        {...({ colorScheme: "red", variant: 'outline', padding: "8px 20px" })}
                        onClick={() => { onOpenCertificateStatus("rejected") }}
                        marginRight="20px"
                        sx={{ outline: 'none !important' }}
                    >
                        {!checkStatus(licenseReview?.status, "rejected") ? "Reject" : "Rejected"}
                    </Button>
                    {/* <Button
                        colorScheme="blue"
                        background="#ffffff"
                        color="#0C1E46"
                        border="1px solid #0C1E46"
                        padding="8px 11px"
                        sx={{ outline: 'none !important' }}
                        onClick={() => {
                            setLicense({})
                            reset()
                        }}
                    >
                        Cancel
                    </Button> */}
                </Box>
            </Box>
            <Box className='sot-approval-wrapper'>
                <Box className='sot-approval-details-wrapper'>
                    <Box className='flex-col' sx={{ width: { base: '100%', lg: '32%' } }}>
                        <TableElement noBorder={true} px={"0"} mb={2}
                            type="review"
                            label={
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    License Number
                                    {(dealer_id) && (
                                        <img
                                            src="/images/ui/blue-link.svg"
                                            alt="License"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                console.log({ dealer_id: dealer_id });
                                                window.open(`/dealers-search/${dealer_id}`, '_blank');
                                            }}
                                            style={{
                                                width: '16px',
                                                height: '16px',
                                                cursor: 'pointer'
                                            }}
                                        />
                                    )}
                                </div>
                            }
                        />
                        <FormControl sx={{ paddingBottom: 4 }}>
                            {renderLicenseId()}
                        </FormControl>
                        <Box className="transfer_flex_row" sx={{ gap: '8px', marginBottom: 4, alignContent: 'flex-end' }}>
                            <Box className={`doc_box ${documentsRequiredDetails?.hasValidCertificate ? 'doc_active' : ''}`}>FFL License</Box>
                            <Box className={`doc_box ${documentsRequiredDetails?.hasValidSOT ? 'doc_active' : ''}`}>SOT</Box>
                            <Box className={`doc_box ${documentsRequiredDetails?.hasValidLOA ? 'doc_active' : ''}`}>LOA</Box>
                        </Box>
                        <TableElement noBorder={true} label={'Principal Business Name'} px={"0"} type={'review'}>
                            <ControlledInput
                                className="sot-tax-stamp__input contact-detail-form__contact-name"
                                type="text"
                                placeholder="Principal Business Name"
                                value={licenseReview?.principal_business_name || ""}
                                onChange={(event) =>
                                    onChangeInputs(
                                        event.target.value,
                                        'principal_business_name'
                                    )
                                }
                            />
                            {licenseReview?.errors && licenseReview?.errors?.principal_business_name ?
                                <div className="">
                                    <div className="edit-error">
                                        {licenseReview.errors && licenseReview?.errors.principal_business_name && (
                                            <>* {licenseReview?.errors.principal_business_name}</>
                                        )}
                                    </div>
                                </div> : ""
                            }
                        </TableElement>
                        <TableElement noBorder={true} label={'Actual Physical Business Name'} px={"0"} type={'review'} mb={2}>
                            <ControlledInput
                                className="sot-tax-stamp__input contact-detail-form__contact-name"
                                type="text"
                                placeholder="Actual Physical Business Name"
                                value={licenseReview?.actual_physical_business_name || ""}
                                onChange={(event) =>
                                    onChangeInputs(
                                        event.target.value,
                                        'actual_physical_business_name'
                                    )
                                }
                            />
                            {licenseReview?.errors && licenseReview?.errors?.actual_physical_business_name ?
                                <div className="">
                                    <div className="edit-error">
                                        {licenseReview.errors && licenseReview?.errors.actual_physical_business_name && (
                                            <>* {licenseReview?.errors.actual_physical_business_name}</>
                                        )}
                                    </div>
                                </div> : ""
                            }
                        </TableElement>
                        <TableElement noBorder={true} label={'Annual Tax Rate'} px={"0"} type={'review'} mb={2}>
                            {!licenseReview?.type_of_operation ? "____" : operationTypes[licenseReview?.type_of_operation as string]?.rate}
                        </TableElement>
                        <TableElement noBorder={true} label={'Number of Locations'} px={"0"} type={'review'} mb={2}>
                            <ControlledInput
                                className="sot-tax-stamp__input contact-detail-form__contact-name"
                                type="number"
                                placeholder="Number of Locations"
                                value={licenseReview?.number_of_locations || ""}
                                onChange={(event) =>
                                    onChangeInputs(
                                        event.target.value,
                                        'number_of_locations'
                                    )
                                }
                            />
                            {licenseReview?.errors && licenseReview?.errors?.number_of_locations ?
                                <div className="">
                                    <div className="edit-error">
                                        {licenseReview.errors && licenseReview?.errors.number_of_locations && (
                                            <>* {licenseReview?.errors.number_of_locations}</>
                                        )}
                                    </div>
                                </div> : ""
                            }
                        </TableElement>
                        <TableElement noBorder={true} label={'SOT Year'} px={"0"} type={'review'} mb={4}>
                            <Box className="sot-year-picker-wrapper">
                                <SOTYearGridPicker
                                    value={licenseReview?.sot_year ?? null}
                                    onChange={onSOTYearChange}
                                    isInvalid={!!(licenseReview?.errors?.sot_year)}
                                />
                            </Box>
                            {licenseReview?.errors?.sot_year ?
                                <div className="">
                                    <div className="edit-error">
                                        * {licenseReview.errors.sot_year}
                                    </div>
                                </div> : ""
                            }
                        </TableElement>

                        <TableElement noBorder={true} label={'Employer Identification Number'} px={"0"} type={'review'} mb={0}>
                            <InputMask
                                id="formattedInput"
                                mask="99-9999999"
                                placeholder="XX-XXXXXXX"
                                value={licenseReview?.employee_identification_number || ""}
                                onChange={(event: any) =>
                                    onChangeInputs(
                                        event.target.value,
                                        'employee_identification_number'
                                    )
                                }
                            >
                                {(inputProps: any) => <input
                                    {...inputProps}
                                    type="text"
                                    className='sot-customize-input'
                                />
                                }
                            </InputMask>
                            {licenseReview?.errors && licenseReview?.errors?.employee_identification_number ?
                                <div className="">
                                    <div className="edit-error">
                                        {licenseReview.errors && licenseReview?.errors.employee_identification_number && (
                                            <>* {licenseReview?.errors.employee_identification_number}</>
                                        )}
                                    </div>
                                </div> : ""
                            }
                        </TableElement>



                    </Box>
                    <Box className='flex-col' sx={{ width: { base: '100%', lg: '32%', md: '45%' } }}>
                        <TableElement noBorder={true} label={'ATF Business Name'} px={"0"} type={'review'} mb="5.6rem">

                            <>
                                {license?.primaryBusinessName || licenseReview?.business_name || '-'}
                                {licenseReview?.errors && licenseReview?.errors?.business_name ?
                                    <div className="">
                                        <div className="edit-error">
                                            {licenseReview.errors && licenseReview?.errors.business_name && (
                                                <>* {licenseReview?.errors.business_name}</>
                                            )}
                                        </div>
                                    </div> : ""
                                }
                            </>
                        </TableElement>
                        <TableElement noBorder={true} label={'Principal Business Address'} px={"0"} type={'review'}>
                            <ControlledInput
                                className="sot-tax-stamp__input contact-detail-form__contact-name"
                                onChange={(event) => {
                                    onChangeInputs(
                                        event.target.value,
                                        'name_and_principal_business_address'
                                    );
                                    setTextareaEvent(event.target.selectionStart);
                                }
                                }
                                value={licenseReview?.name_and_principal_business_address || ""}
                                name="name_and_principal_business_address"
                            />
                            {/* <Textarea
                                onChange={(event) => {
                                    onChangeInputs(
                                        event.target.value,
                                        'name_and_principal_business_address'
                                    );
                                    setTextareaEvent(event.target.selectionStart);
                                }
                                }
                                value={licenseReview?.name_and_principal_business_address || ""}
                                name="name_and_principal_business_address"
                                className="sot-transfer-form__textarea"
                                ref={principalBusinessAddress as any}
                            /> */}
                            {licenseReview?.errors && licenseReview?.errors?.name_and_principal_business_address ?
                                <div className="">
                                    <div className="edit-error">
                                        {licenseReview.errors && licenseReview?.errors.name_and_principal_business_address && (
                                            <>* {licenseReview?.errors.name_and_principal_business_address}</>
                                        )}
                                    </div>
                                </div> : ""
                            }
                        </TableElement>
                        <TableElement noBorder={true} label={'Actual Physical Business Address'} px={"0"} type={'review'} >
                            <ControlledInput
                                className="sot-tax-stamp__input contact-detail-form__contact-name"
                                onChange={(event) => {
                                    onChangeInputs(
                                        event.target.value,
                                        'actual_physical_business_address'
                                    );
                                    setTextareaEvent(event.target.selectionStart);
                                }
                                }
                                value={licenseReview?.actual_physical_business_address || ""}
                                name="actual_physical_business_address"
                            />
                            {/* <Textarea
                                onChange={(event) => {
                                    onChangeInputs(
                                        event.target.value,
                                        'actual_physical_business_address'
                                    );
                                    setTextareaEvent(event.target.selectionStart);
                                }
                                }
                                value={licenseReview?.actual_physical_business_address || ""}
                                name="actual_physical_business_address"
                                className="sot-transfer-form__textarea"
                                ref={physicalBusinessAddress as any}
                            /> */}
                            {licenseReview?.errors && licenseReview?.errors?.actual_physical_business_address ?
                                <div className="">
                                    <div className="edit-error">
                                        {licenseReview.errors && licenseReview?.errors.actual_physical_business_address && (
                                            <>* {licenseReview?.errors.actual_physical_business_address}</>
                                        )}
                                    </div>
                                </div> : ""
                            }
                        </TableElement>
                        <TableElement noBorder={true} label={'Types of Operation Conducted'} px={"0"} type={'review'} mb={'3.3rem'}>
                            <Select
                                placeholder='Select option'
                                sx={{ borderColor: "black" }}
                                onChange={(event) =>
                                    onChangeInputs(
                                        event.target.value,
                                        'type_of_operation'
                                    )
                                }
                            >
                                {
                                    Object.entries(operationTypes).map(([key, val]) => <option value={key} selected={key === licenseReview?.type_of_operation}>({key}) {val.name}</option>)
                                }
                            </Select>
                            {licenseReview?.errors && licenseReview?.errors?.type_of_operation ?
                                <div className="">
                                    <div className="edit-error">
                                        {licenseReview.errors && licenseReview?.errors.type_of_operation && (
                                            <>* {licenseReview?.errors.type_of_operation}</>
                                        )}
                                    </div>
                                </div> : ""
                            }
                        </TableElement>

                        <TableElement noBorder={true} label={'Receipt Date'} px={"0"} mb={"16px"} type={'review'} >
                            <FlatDatePickr
                                label=""
                                value={fixTimezoneOffset(licenseReview?.date_of_tax_stamp_receipt) || null}
                                onDateChange={(date: Date) => onDateChange(date, 'date_of_tax_stamp_receipt')}
                                classNameValue="transfer-form__input receipt-date-picker"
                            />
                            {licenseReview?.errors && licenseReview?.errors?.date_of_tax_stamp_receipt ?
                                <div className="">
                                    <div className="edit-error">
                                        {licenseReview.errors && licenseReview?.errors.date_of_tax_stamp_receipt && (
                                            <>* {licenseReview?.errors.date_of_tax_stamp_receipt}</>
                                        )}
                                    </div>
                                </div> : ""
                            }
                        </TableElement>
                        <TableElement noBorder={true} label={'Control Number'} px={"0"} type={'review'} mb={0}>
                            <ControlledInput
                                className="sot-tax-stamp__input contact-detail-form__contact-name"
                                type="text"
                                placeholder="e.g. 2025122-N70-005 or 2025S1845"
                                value={licenseReview?.display_control_number || ""}
                                onChange={(event: any) =>
                                    onChangeInputs(
                                        event.target.value?.trim(),
                                        'display_control_number'
                                    )
                                }
                            />
                        </TableElement>

                        {/* <TableElement label={'ATF Premises Address'} type="review">
                            {documentsRequiredDetails?.premiseAddress?.street && <>
                                {documentsRequiredDetails?.premiseAddress?.street}, {documentsRequiredDetails?.premiseAddress?.city}, {documentsRequiredDetails?.premiseAddress?.state}, {documentsRequiredDetails?.premiseAddress?.zipCode}
                            </>}
                        </TableElement>
                        <TableElement label={'ATF Mailing Address'} type="review">
                            {documentsRequiredDetails?.mailingAddress?.street && <>
                                {documentsRequiredDetails?.mailingAddress?.street}, {documentsRequiredDetails?.mailingAddress?.city}, {documentsRequiredDetails?.mailingAddress?.state}, {documentsRequiredDetails?.mailingAddress?.zipCode}
                            </>}
                        </TableElement> */}

                    </Box>
                    <Box className='flex-col' sx={{ width: { base: '100%', lg: '32%', md: '45%' } }}>
                        <TableElement label={'Status'} type="review" mb={3}>
                            {licenseReview?.status}
                        </TableElement>
                        <TableElement label={'Source of SOT'} type="review" mb={3}>
                            {getSourceTextForApprovalPage(licenseReview?.source, 'SOT')}
                        </TableElement>
                        <TableElement label={'Uploaded Date'} type="review" mb={3}>
                            {licenseReview?.tax_stamp_upload_date
                                ? getFormattedDateTime(new Date(licenseReview?.tax_stamp_upload_date)) : ''}
                        </TableElement>
                        <TableElement label={'Failure Reason'} type="review" mb={3}>
                            {getFailedStep(licenseReview?.error_code || null)}
                        </TableElement>
                        <TableElement noBorder={true} label={'Rejection Reason'} px={"0"} type={'review'}>
                                <Select
                                    placeholder='Select reason*'
                                    my={3}
                                    onChange={(event) => {
                                        setReviewConfirmation({
                                            ...reviewConfirmation,
                                            rejectedReason: event.target.value,
                                        })
                                        setReviewInputVal({ ...reviewInputVal, rejectReason: event.target.value })
                                    }}
                                    disabled={!checkStatus(licenseReview?.status, "rejected")}
                                    required
                                >{
                                        Object.entries(reviewRejectedReasonsSot).map(([key, val]) => <option value={val as string} selected={val === reviewInputVal?.rejectReason}>{val as string}</option>)
                                    }
                                </Select>
                        </TableElement>
                        <TableElement noBorder={true} label={'Rejection Note'} px={"0"} type='review'>
                            <Textarea
                                // isDisabled={true}
                                name="rejected_reason"
                                className="sot-transfer-form__textarea"
                                value={reviewInputVal?.note || ""}
                                disabled={checkStatus(licenseReview?.status, "pending")}
                                onChange={(e) => {
                                    setReviewConfirmation({
                                        ...reviewConfirmation,
                                        note: e.target.value,
                                    })
                                    setReviewInputVal({
                                        ...reviewInputVal,
                                        note: e.target.value,
                                    })
                                }}
                            />
                        </TableElement>
                        <TableElement noBorder={true} label={'Manual Review Requester'} px={"0"} type={'review'}>
                            <Box>{licenseReview?.action_performed_by
                                ? getFormattedTextForManualReview(licenseReview?.action_performed_by, licenseReview?.manual_review_requested_by) : ''}</Box>
                        </TableElement>
                        <TableElement noBorder={true} label={'Manual Review Request Date'} px={"0"} type={'review'}>
                            <Box>{licenseReview?.manual_review_requested_at ? getFormattedDateTime(licenseReview?.manual_review_requested_at) : ''}</Box>
                        </TableElement>
                        {/* <TableElement noBorder={true} label={'Bio Metric Scanner:'} px={"0"} type={'review'}>
                            <Switch
                                size='md'
                                onChange={(event) =>
                                    onChangeInputs(
                                        event.target.checked,
                                        'bio_metric_scanner'
                                    )
                                }
                                isChecked={!!licenseReview?.bio_metric_scanner}
                            />
                            {licenseReview?.errors && licenseReview?.errors?.bio_metric_scanner ?
                                <div className="">
                                    <div className="edit-error">
                                        {licenseReview.errors && licenseReview?.errors.bio_metric_scanner && (
                                            <>* {licenseReview?.errors.bio_metric_scanner}</>
                                        )}
                                    </div>
                                </div> : ""
                            }
                        </TableElement> */}
                    </Box>
                </Box>
                <Box className='sot-preivew-wrapper'>
                    {
                        licenseReview?.license_path && !(licenseReview?.license_path.split('.').pop() === 'pdf') ?
                            <MapInteractionCSS
                                showControls
                                defaultValue={{
                                    scale: 1,
                                    translation: { x: 0, y: 20 }
                                }}
                                minScale={0.5}
                                maxScale={3}
                                translationBounds={{
                                    xMax: 400,
                                    yMax: 200
                                }}
                                btnClass={'map-interection-buttons'}
                            >
                                <Image src={licenseReview?.license_path ? fullURL(licenseReview?.license_path) : ""} alt='sot-image' className={`sot-image`} />
                            </MapInteractionCSS>
                            :
                            <iframe src={licenseReview?.license_path ? fullURL(licenseReview?.license_path) : ""} name={"SOT License Preview"} title={"SOT License Preview"} id='sot-license-iframe' className='sot-license-preview'></iframe>
                    }
                </Box>
            </Box>
            <ReviewConfirmationModal
                isOpen={isReviewConfirmationMadalOpen}
                onClose={() => {
                    setReviewConfirmation({
                        ...reviewConfirmation,
                        rejectedReason: '',
                        note: '',
                        error: '',
                    })
                    closeReviewConfirmationModal()
                }}
                modalHeader={reviewConfirmation.header}
                isCancelBtnDisable={reviewConfirmation.isCancelBtnDisable}
                isConfirmBtnDisable={disableConfirmButton()}
                isConfirmBtnLoading={reviewConfirmation.isConfirmBtnLoading}
                onClickConfirmBtn={(isEmailIgnored) => { updateCertificateStatus(reviewConfirmation.status, false, isEmailIgnored) }}
                fields={reviewRejectedReasonsSot}
                selectedValue={reviewConfirmation.rejectedReason}
                handleChange={handleChange}
                handleTextChange={handleTextChange}
                hideDropdown={reviewConfirmation.status !== "approved"}
                error={reviewConfirmation.error}
                approvalDetails={licenseReview}
            />
            <ModalComponent
                footer={null}
                isOpen={isSearchNewDealerModalOpen}
                onClose={() => {
                    onCloseSearchNewDealerModal()
                    setIsLoading(false)
                }}
                children={
                    <>
                        <ExploreV2
                            cognitoGroup={false}
                            isSuperAdmin={false}
                            isStoreAdmin={true}
                            isAssignDealerModal={true}
                            assignNewDealerInitialZipCode={licenseReview?.license_key as string}
                            onCloseAssignDealerModal={() => {
                                onCloseSearchNewDealerModal();
                            }}
                            type={"admin-approval"}
                            setLicenseKey={callLicenseDetails}
                        />
                        {isAssigningTransferDealer && <ModalLoader />}
                    </>
                }
                title={"Select New License"}
                variant={2}
                maxWidth='1360px'
                className="assign-new-dealer-modal"
            />
        </Box>
    )
}

export default SOTApprovalPage